import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { RootStoreState } from '@app/root-store';
import * as selectors from '../../../../../root-store/relationship-store/relationship.selector';
import * as loginSelectors from '../../../../../root-store/login-store/login.selector';
import { ShareService } from '@app/shared/services/vault/share.service';
import { FileEncryptedSuccessComponent } from '@app/modules/vault/components/dialogs/file-encrypted-success/file-encrypted-success.component';
import { MatDialog } from '@angular/material/dialog';
import { stringDateToDatabase } from '@app/shared/utils';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'accessibility',
  templateUrl: './accessibility.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessibilityComponent implements OnInit, OnDestroy {
  @Input() data;
  @Output() dAccessibilityEmitter: EventEmitter<object> = new EventEmitter();

  stateObject;
  screenWidth = 0;
  searchUser = '';
  selectedUsers = [];
  allConnections: any = [];
  unfilteredUsersArray: any = [];
  subscription = new Subscription();
  resizeObservable$: Observable<Event>;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private shareService: ShareService,
    private store$: Store<RootStoreState.State>,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef
  ) {
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe((result) => {
        if (result) {
          this.stateObject = result;
        } else {
          this.stateObject = null;
        }
      })
    );
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.subscription.add(
      this.resizeObservable$.subscribe((evt) => {
        this.screenWidth = evt.target['innerWidth'];
      })
    );
    this.getSharedFormUserList();
    this.getAndFilterAllUsers();
  }

  getAndFilterAllUsers() {
    this.subscription.add(
      this.store$.pipe(select(selectors.getAndFilterAllUsers())).subscribe((res) => {
        const tempEllemt = res.map((element) => ({
          ...element,
          name: element?.user_first_name + ' ' + element?.user_last_name,
          isAccess: true,
          connection_status: 'approved',
        }));
        this.allConnections = tempEllemt;
        this.unfilteredUsersArray = this.allConnections;
        this.cd.detectChanges();
      })
    );
  }

  getSharedFormUserList() {
    this.subscription.add(
      this.shareService.getSharedFormUserList(this.data.vault_user_form_id, this.data.user_id).subscribe(
        (res) => {
          let updatedObject = [];
          res.forEach((element, index) => {
            let tempEllemt = {
              ...element,
              name: element?.user_first_name + ' ' + element?.user_last_name,
            };
            tempEllemt['vault_user_share_is_sealed'] = element.vault_user_share_is_sealed == 1 ? true : false;
            updatedObject.push(tempEllemt);
          });
          this.selectedUsers = updatedObject;
          this.allConnections = this.allConnections.filter((item1) => !this.selectedUsers.some((item2) => item2.user_id === item1.user_id));
          this.subscription.add(
            this.store.select(selectors.getrelationshipnameList(this.selectedUsers)).subscribe((item) => {
              this.selectedUsers = item;
              this.cd.detectChanges();
            })
          );
        },
        (error: any) => {
          this.toastr.error('Error occurred while getting sharing');
        }
      )
    );
  }

  addUserToShareWith(item) {
    const payload = {
      connection_status: item.connection_status,
      user_id: this.data.user_id,
      shared_with_user_id: item.user_id,
      end_date: null,
      share_date: stringDateToDatabase(new Date(Date.now()).toLocaleDateString()),
      is_sealed: false,
      vault_user_form_id: this.data.vault_user_form_id,
      is_editable: false,
    };
    this.subscription.add(
      this.shareService.shareForm(payload).subscribe(async (results) => {
        if (results?.securityKey) {
          this.dialog.open(FileEncryptedSuccessComponent, {});
        }
        this.toastr.success(` ${this.data?.form_name} shared with ${item?.name}.`);
        this.getSharedFormUserList();
        this.cd.detectChanges();
      })
    );
  }

  updateUserSharedWith(existingUser) {
    const payload = {
      user_id: this.data.user_id,
      id: existingUser.vault_user_share_id,
      share_date: stringDateToDatabase(new Date(Date.now()).toLocaleDateString()),
      is_sealed: existingUser.vault_user_share_is_sealed,
      is_editable: false,
      end_date: null,
    };
    this.subscription.add(
      this.shareService.updateFormShare(payload).subscribe(async (res) => {
        this.toastr.success(`${existingUser?.name}'s access updated for ${this.data?.form_name} `);
        const index = this.selectedUsers.findIndex((user) => user.vault_user_share_id == existingUser.vault_user_share_id);
        this.selectedUsers[index].vault_user_share_is_sealed = res.is_sealed;
        this.cd.detectChanges();
      })
    );
  }

  deleteUserSharedWith(existingUser) {
    const payload = {
      user_id: this.data.user_id,
      id: existingUser.vault_user_share_id,
    };
    this.subscription.add(
      this.shareService.deleteFormShare(payload).subscribe((res) => {
        this.selectedUsers = this.selectedUsers.filter((user) => user.vault_user_share_id != existingUser.vault_user_share_id);
        this.allConnections = this.unfilteredUsersArray.filter((item1) => !this.selectedUsers.some((item2) => item2.user_id === item1.user_id));
        this.toastr.success(`${existingUser?.name}'s access removed for ${this.data?.form_name} File `);
        this.cd.detectChanges();
      })
    );
  }

  handleSelectedUser(item: any, actionType: string) {
    const exist = this.selectedUsers.find((user) => user?.user_id === item?.user_id);
    if (exist) {
      event.preventDefault();
    } else {
      this.addUserToShareWith(item);
    }
  }

  handleNotificationBell(user: any) {
    user.vault_user_share_is_sealed = !user.vault_user_share_is_sealed;
    this.updateUserSharedWith(user);
  }

  handleSteps(event) {
    this.dAccessibilityEmitter.emit({ status: event, id: this.data?.id });
  }

  connectionCreated(event) {
    const item = {
      user_id: event.id,
      connection_status: event.status,
      user_profile_image_url: event.profile_image_url,
      name: event.name,
    };
    this.addUserToShareWith(item);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
